import { ActionText, StackingContext, Text } from "@mdotm/shadcn/legacy/components";
import { useDraggable } from "@mdotm/shadcn/legacy/headless";
import { type NodeOrFn, useDrivenState, useRefProxy, useUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { type Rect2D, type Vec2D, clamp, isBetween, steppify } from "@mdotm/shadcn/legacy/utils";
import { identity } from "@mdotm/utils/functions";
import type { SeriesColumnOptions } from "highcharts";
import HighchartsReact, { type HighchartsReactRefObject } from "highcharts-react-official";
import { Set } from "immutable";
import { type ForwardedRef, useEffectEvent, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { renderToString } from "react-dom/server";
import { Divider } from "$root/components/Divider";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import { avgArrayLike, maxArrayLike, minArrayLike } from "$root/utils";
import {
	ChartCardShell,
	type ChartHeight,
	ChartTitle,
	type ChartWallStatus,
	ChartWallSwitch,
	chartHeightInPx,
} from "./ChartCardShell";
import { applyBandSelection, handleSelectionClick } from "./chart-selection";
import { calculatePointWidth } from "./point-width";

export type MetricComparisonChartItem = {
	name: string;
	metrics: [number | null | undefined, number | null | undefined];
	missingReason?: [string | null | undefined, string | null | undefined];
	id: string;
};
export type MetricComparisonChartProps = {
	innerRef?: ForwardedRef<HighchartsReactRefObject>;
} & {
	/** @default 1  */
	thresholdDragStep?: number;
	chartTitle: string;
	mainMetricLabel: string;
	secondaryMetricLabel: string;
	midActions?: NodeOrFn;
	actions?: NodeOrFn;
	threshold?: number;
	onThresholdChange?(newThreshold: number): void;
	/** e.g. % */
	unit?: string;
	data: Array<MetricComparisonChartItem>;
	selectedIds?: Set<string> | null;
	onSelectionChange?(newSelection: Set<string>): void;
	/** @default 'tall' */
	chartHeight?: ChartHeight;
	highlightSecondaryMetric?: boolean;
	disable?: boolean;
	tooltipTitleHrefProvider?(itemId: string): string;
};

const NUM_SERIES = 2;

export function MetricComparisonChart(props: MetricComparisonChartProps): React.JSX.Element {
	const highchartsRef = useRefProxy(null, props.innerRef);
	const unit = props.unit ?? "";
	const { formatNumber } = useLocaleFormatters();

	const thresholdBoxId = useUniqueDOMId();

	const flattedMetrics = useMemo(
		() => props.data.flatMap((x) => x.metrics.filter((metric) => metric != null)),
		[props.data],
	);
	const [visibleCount, setVisibleCount] = useState(props.data.length);

	const { min, avg, max } = useMemo(() => {
		if (flattedMetrics.length === 0) {
			return { min: 0, max: 0, avg: 0 };
		}

		return {
			min: minArrayLike(flattedMetrics, (metric) => metric),
			avg: avgArrayLike(flattedMetrics, (metric) => metric),
			max: maxArrayLike(flattedMetrics, (metric) => metric),
		};
	}, [flattedMetrics]);

	const [threshold, setThreshold] = useDrivenState(props.threshold);

	/**
	 * The two props Highcharts reaches through a *callback* rather than through a value, at a stable identity so
	 * `chartOptions` can depend on the props it reads instead of on `props` wholesale — see the note on that
	 * memo's dependencies. Neither is stable at the call site, where both are written inline.
	 */
	const emitSelectionChange = useEffectEvent((newSelection: Set<string>) => props.onSelectionChange?.(newSelection));
	const tooltipTitleHrefOf = useEffectEvent((itemId: string) => props.tooltipTitleHrefProvider?.(itemId));
	const cachedAxisYLimits = useMemo(
		() => ({
			max: props.threshold ? Math.max(max, props.threshold) : max,
			min: props.threshold ? Math.min(min, props.threshold) : min,
		}),
		[max, min, props.threshold],
	);

	const [selectedIds, setSelectedIds] = useDrivenState(props.selectedIds ?? Set<string>(), {
		equalityComparator: (a, b) => a.equals(b),
	});
	const [selectionRect, setSelectionRect] = useState<(Vec2D & Rect2D) | null>(null);
	const shiftSelectionIdRef = useRef<string | null>(null);

	const chartId = useUniqueDOMId();

	const classPrefix = useUniqueDOMId();
	const thresholdDragStep = props.thresholdDragStep ?? 1;

	const defaultHeightForNonRapresentableValue = useMemo(
		() => calculatePlaceholderHeight(cachedAxisYLimits.max, 0.01),
		[cachedAxisYLimits.max],
	);
	const { currentSeriesData, proposalSeriesData } = useMemo(() => {
		const main: Highcharts.PointOptionsObject[] = [];
		const secondary: Highcharts.PointOptionsObject[] = [];

		props.data.forEach(({ name, metrics: [metricA, metricB], id }, i) => {
			// TODO: improve chart interaction
			const basePoint = {
				name,
				selected: selectedIds.has(id),
				className: `${classPrefix}-column ${classPrefix}-column-index-${i}`,
				color: themeCSSVars.palette_S400,
			} satisfies Highcharts.PointOptionsObject;

			const metricABasePoint = {
				...basePoint,
				className: basePoint.className.concat(
					[
						"",
						selectedIds.has(id)
							? "fill-[color:var(--palette-P400)] hover:fill-[color:var(--palette-P100)]"
							: "hover:fill-[color:var(--palette-S100)]",
						props.highlightSecondaryMetric ? "opacity-40" : "",
					].join(" "),
				),
			};

			const hasSecondaryMetric = metricB != null;

			// Current (metricA): ghost / zero / normal
			if (metricA == null) {
				main.push({
					...metricABasePoint,
					y: defaultHeightForNonRapresentableValue,
					color: themeCSSVars.palette_N100,
					borderColor: themeCSSVars.palette_N300,
					pointWidth: calculatePointWidth({
						visibleCount,
						metricType: "main",
						metricState: "null",
						hasSecondaryMetric,
					}),
				});
			} else if (metricA === 0) {
				main.push({
					...metricABasePoint,
					y: defaultHeightForNonRapresentableValue,
					pointWidth: calculatePointWidth({
						visibleCount,
						metricType: "main",
						metricState: "zero",
						hasSecondaryMetric,
					}),
				});
			} else {
				main.push({
					...metricABasePoint,
					y: metricA,
					pointWidth: calculatePointWidth({
						visibleCount,
						metricType: "main",
						metricState: "normal",
						hasSecondaryMetric,
					}),
				});
			}

			// Proposal (metricB): ghost / zero / normal
			if (metricB == null) {
				secondary.push({
					...basePoint,
					color: themeCSSVars.palette_B400,
					y: null,
				});
			} else if (metricB === 0) {
				secondary.push({
					...basePoint,
					color: themeCSSVars.palette_B400,
					y: defaultHeightForNonRapresentableValue,
					pointWidth: calculatePointWidth({
						visibleCount,
						metricType: "secondary",
						metricState: "zero",
					}),
				});
			} else {
				secondary.push({
					...basePoint,
					color: themeCSSVars.palette_B400,
					y: metricB,
				});
			}
		});

		return { currentSeriesData: main, proposalSeriesData: secondary };
	}, [
		props.data,
		selectedIds,
		classPrefix,
		props.highlightSecondaryMetric,
		visibleCount,
		defaultHeightForNonRapresentableValue,
	]);

	const draggingStateRef = useRef<
		| {
				type: "selection";
				subType: "toggle" | "new";
				initialSelection: typeof selectedIds;
				currentSelection: typeof selectedIds;
				seriesContainers: (SVGGElement | null)[];
		  }
		| {
				type: "threshold";
				initialThreshold: number;
				thresholdBox: DOMRect;
				plotDomArea: Vec2D & Rect2D;
				plotCartesianArea: Vec2D & Rect2D;
		  }
		| null
	>(null);

	const tooltipId = useUniqueDOMId();

	const { dragging } = useDraggable({
		draggableEl: props.disable ? undefined : window,
		filter(initialCoords, keyModifiers) {
			const tooltipBox = document.getElementById(tooltipId)?.getBoundingClientRect();
			if (
				tooltipBox &&
				isBetween(initialCoords.x, tooltipBox.left, tooltipBox.left + tooltipBox.width) &&
				isBetween(initialCoords.y, tooltipBox.top, tooltipBox.top + tooltipBox.height)
			) {
				return false;
			}
			const thresholdBox = document.getElementById(thresholdBoxId)?.getBoundingClientRect();
			const cardAreaEl = document.getElementById(chartId)!;
			const plotDomArea = {
				x: highchartsRef.current?.chart.plotLeft ?? 0,
				y: highchartsRef.current?.chart.plotTop ?? 0,
				width: highchartsRef.current?.chart.plotWidth ?? 1,
				height: highchartsRef.current?.chart.plotHeight ?? 1,
			};

			const [xMin, xMax, yMin, yMax] = [
				highchartsRef.current?.chart.xAxis[0].min ?? 0,
				highchartsRef.current?.chart.xAxis[0].max ?? 1,
				highchartsRef.current?.chart.yAxis[0].min ?? 0,
				highchartsRef.current?.chart.yAxis[0].max ?? 1,
			];

			const plotCartesianArea = {
				x: xMin,
				y: yMin,
				width: xMax - xMin,
				height: yMax - yMin,
			};
			if (
				thresholdBox &&
				isBetween(initialCoords.x, thresholdBox.left, thresholdBox.left + thresholdBox.width) &&
				isBetween(initialCoords.y, thresholdBox.top, thresholdBox.top + thresholdBox.height)
			) {
				draggingStateRef.current = {
					type: "threshold",
					initialThreshold: threshold ?? 0,
					thresholdBox,
					plotDomArea,
					plotCartesianArea,
				};
				return true;
			}
			const svgWrapper = cardAreaEl.querySelector(".highcharts-container")!.getBoundingClientRect();
			const navigator = cardAreaEl.querySelector(".highcharts-navigator")!.getBoundingClientRect();
			if (
				isBetween(initialCoords.x, svgWrapper.left, svgWrapper.left + svgWrapper.width) &&
				isBetween(initialCoords.y, svgWrapper.top, navigator.top)
			) {
				draggingStateRef.current = {
					type: "selection",
					subType: keyModifiers.shift ? "toggle" : "new",
					initialSelection: selectedIds,
					currentSelection: selectedIds,
					seriesContainers: Array.from({ length: NUM_SERIES }, (_, i) =>
						cardAreaEl.querySelector<SVGGElement>(`.highcharts-series-${i}`),
					),
				};
				return true;
			}
			return false;
		},
		preventDefault: ({ phase }) => phase !== "begin",
		stopPropagation: ({ phase }) => phase !== "begin",
		onDragMove(initial, current) {
			if (!draggingStateRef.current) {
				return;
			}
			switch (draggingStateRef.current.type) {
				case "selection": {
					const newSelectionRect = {
						x: Math.min(initial.x, current.x),
						y: Math.min(initial.y, current.y),
						width: Math.abs(current.x - initial.x),
						height: Math.abs(current.y - initial.y),
					};
					if (newSelectionRect.width === 0 || newSelectionRect.height === 0) {
						return;
					}
					const selectionHDirection = current.x - initial.x > 0 ? "right" : "left";
					setSelectionRect(newSelectionRect);
					let indicesInArea = Set<number>();
					for (let i = 0; i < props.data.length; i++) {
						for (const container of draggingStateRef.current.seriesContainers) {
							if (!container) continue;
							const candidate = container.querySelector(`.${classPrefix}-column-index-${i}`)?.getBoundingClientRect();
							if (candidate && intersect(domRectToRect2D(candidate), newSelectionRect)) {
								indicesInArea = indicesInArea.add(i);
							}
						}
					}
					const idsInArea = indicesInArea.map((i) => props.data[i].id);

					if (indicesInArea.size > 0) {
						shiftSelectionIdRef.current =
							selectionHDirection === "right"
								? props.data[minArrayLike(indicesInArea.toArray(), identity)].id
								: props.data[maxArrayLike(indicesInArea.toArray(), identity)].id;
					}

					draggingStateRef.current.currentSelection = applyBandSelection(
						draggingStateRef.current.initialSelection ?? Set<string>(),
						idsInArea,
						draggingStateRef.current.subType,
					);
					setSelectedIds(draggingStateRef.current.currentSelection);
					break;
				}
				case "threshold": {
					setThreshold(
						steppify(
							clamp(
								draggingStateRef.current.initialThreshold -
									((current.y - initial.y) / draggingStateRef.current.plotDomArea.height) *
										draggingStateRef.current.plotCartesianArea.height,
								draggingStateRef.current.plotCartesianArea.y,
								draggingStateRef.current.plotCartesianArea.height + draggingStateRef.current.plotCartesianArea.y,
							),
							thresholdDragStep,
						),
					);
					break;
				}
			}
		},
		onDragEnd() {
			if (!draggingStateRef.current) {
				return;
			}
			switch (draggingStateRef.current.type) {
				case "selection": {
					if (!draggingStateRef.current.currentSelection?.equals(draggingStateRef.current.initialSelection)) {
						emitSelectionChange(draggingStateRef.current.currentSelection ?? Set());
					}
					setSelectionRect(null);
					break;
				}
				case "threshold": {
					if (threshold != null) {
						props.onThresholdChange?.(threshold);
					}
					break;
				}
			}
		},
	});

	/**
	 * Memoized for the same reason the options are: `containerProps` is the *other* entry in the dependency
	 * list of the layout effect that calls `chart.update`, so an object literal here re-inits every series on
	 * every render however stable the options object is.
	 */
	const containerProps = useMemo(
		() => ({
			style: {
				height: chartHeightInPx(props.chartHeight ?? "tall"),
				width: "100%",
			},
			className: "cursor-crosshair !overflow-visible",
		}),
		[props.chartHeight],
	);

	const chartOptions = useMemo((): Highcharts.Options => {
		return {
			chart: {
				type: "column",
				style: {
					fontFamily: "Gotham,sans-serif",
					overflow: "visible",
				},
			},
			title: { text: "" },
			legend: { enabled: false },
			xAxis: {
				labels: { enabled: false },
				tickWidth: 0,
				events: {
					afterSetExtremes(event) {
						if (event.min != null && event.max != null) {
							const minIndex = Math.max(0, Math.floor(event.min));
							const maxIndex = Math.min(props.data.length - 1, Math.ceil(event.max));
							setVisibleCount(maxIndex + 1 - minIndex);
						}
					},
				},
			},
			tooltip: {
				enabled: !dragging,
				useHTML: true,
				padding: 0,
				borderWidth: 0,
				shared: true,
				shadow: true,
				stickOnContact: true,
				followPointer: false,
				positioner(this, tooltipWidth, tooltipHeight, point) {
					const zeroPixelY = this.chart.yAxis[0].toPixels(0, false);
					const dataIndex = this.chart.hoverPoint?.index;
					const dataItem = props.data[dataIndex ?? 0].metrics[0];
					const metricA = dataItem ?? 0;
					const isZeroAbove80 = zeroPixelY < this.chart.plotTop + this.chart.plotHeight / 5;

					return {
						x: clamp(
							point.plotX + this.chart.plotLeft - tooltipWidth / 2,
							this.chart.plotLeft,
							this.chart.plotLeft + this.chart.plotWidth - tooltipWidth,
						),
						y:
							metricA === 0
								? isZeroAbove80
									? this.chart.plotTop + point.plotY + 10
									: this.chart.plotTop + point.plotY - tooltipHeight - 10
								: metricA < 0
									? this.chart.plotTop + point.plotY + 10
									: this.chart.plotTop + point.plotY - tooltipHeight - 10,
					};
				},
				formatter(this) {
					if (!this.points || this.points.length === 0) {
						return "";
					}
					const dataIndex = this.points[0].point.index;
					const dataItem = props.data[dataIndex];
					const [metricA, metricB] = dataItem.metrics;
					const [missingReasonA, missingReasonB] = dataItem.missingReason ?? [];
					// Resolved once, and branched on rather than on the provider's presence: `tooltipTitleHrefOf` is
					// defined whether or not the caller passed a provider, so the href it returns is the only thing that
					// says whether the title is a link.
					const titleHref = tooltipTitleHrefOf(dataItem.id);

					return renderToString(
						<div
							className="p-2 rounded-sm bg-white min-w-60 shadow-sm pointer-events-auto flex flex-col gap-2"
							id={tooltipId}
						>
							<div className="py-1 rounded-sm bg-[#EFF0F3] text-center">
								{titleHref != null ? (
									<ActionText href={titleHref} type="Body/M/Bold">
										{dataItem.name}
									</ActionText>
								) : (
									<Text type="Body/M/Bold">{dataItem.name}</Text>
								)}
							</div>
							<div className="flex justify-between">
								<div className="flex items-center gap-1">
									<ColoredRectangle variant="vertical" color={themeCSSVars.palette_S400} />
									<Text type="Body/M/Book">{props.mainMetricLabel}</Text>
								</div>
								<Text type="Body/M/Bold">
									{metricA != null
										? `${formatNumber(metricA, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`
										: "N/A"}
								</Text>
							</div>
							{metricA == null && missingReasonA && <Text type="Body/S/Italic">{missingReasonA}</Text>}
							{(metricB != null || missingReasonB) && <Divider direction="horizontal" />}
							{(metricB != null || missingReasonB) && (
								<div className="flex justify-between">
									<div className="flex items-center gap-1">
										<ColoredRectangle variant="vertical" color={themeCSSVars.palette_B400} />
										<Text type="Body/M/Book">{props.secondaryMetricLabel}</Text>
									</div>
									<Text type="Body/M/Bold">
										{metricB != null
											? `${formatNumber(metricB, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`
											: "N/A"}
									</Text>
								</div>
							)}
							{metricB == null && missingReasonB && <Text type="Body/S/Italic">{missingReasonB}</Text>}
						</div>,
					);
				},
			},

			credits: { enabled: false },
			yAxis: {
				gridLineDashStyle: "Dash",
				// max: cachedAxisYLimits.max,
				// min: cachedAxisYLimits.min,
				title: { text: "" },
				labels: {
					formatter(ctx) {
						return `${formatNumber(ctx.value, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`;
					},
				},
				plotLines:
					threshold == null
						? []
						: [
								{
									value: threshold,
									color: "rgba(135, 146, 171, 0.5)",
									zIndex: 10,
									label: {
										align: "right",
										useHTML: true,
										formatter() {
											return renderToString(
												<div
													id={thresholdBoxId}
													className="border-2 p-1 bg-white rounded-sm flex items-center gap-1"
													style={{
														borderColor: themeCSSVars.palette_N200,
													}}
												>
													<svg
														width="16"
														height="16"
														viewBox="0 0 16 16"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
														role="presentation"
													>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M10.2227 2.16455C10.6494 2.16529 11.0337 2.42263 11.1968 2.81683C11.36 3.21103 11.27 3.66468 10.9687 3.96673L8.74557 6.19053C8.33329 6.60179 7.66594 6.60179 7.25367 6.19053L5.03092 3.96711C4.72962 3.66507 4.63923 3.21103 4.80239 2.81683C4.96556 2.42263 5.34985 2.16529 5.77649 2.16455L10.2227 2.16455ZM10.2608 3.26038C10.2768 3.2443 10.2815 3.22021 10.2729 3.19928C10.2642 3.17835 10.2438 3.16467 10.2212 3.16455H5.77802C5.75539 3.16467 5.73503 3.17835 5.72637 3.19928C5.7177 3.22022 5.72246 3.24432 5.73843 3.2604L7.95991 5.48255C7.98182 5.50427 8.01729 5.5044 8.0392 5.48268L10.2608 3.26038Z"
															fill="#697796"
														/>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M2.16406 8.00011C2.16406 7.72396 2.38792 7.50011 2.66406 7.50011H13.3352C13.6113 7.50011 13.8352 7.72396 13.8352 8.00011C13.8352 8.27625 13.6113 8.50011 13.3352 8.50011H2.66406C2.38792 8.50011 2.16406 8.27625 2.16406 8.00011Z"
															fill="#697796"
														/>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M7.95991 10.5177L5.73852 12.7397C5.72255 12.7558 5.7177 12.78 5.72637 12.8009C5.73503 12.8219 5.7554 12.8355 5.77804 12.8357H10.2212C10.2438 12.8355 10.2642 12.8219 10.2729 12.8009C10.2815 12.78 10.2768 12.7559 10.2608 12.7398L8.03933 10.5177C8.01741 10.4959 7.98182 10.4959 7.95991 10.5177ZM7.25367 9.80969C7.66594 9.39843 8.33329 9.39843 8.74557 9.80969L8.74606 9.81017L10.9683 12.0331C11.2696 12.3351 11.36 12.7892 11.1968 13.1834C11.0337 13.5776 10.6494 13.8349 10.2227 13.8357H5.77649C5.34985 13.8349 4.96556 13.5776 4.80239 13.1834C4.63923 12.7892 4.72924 12.3355 5.03054 12.0335L7.25367 9.80969Z"
															fill="#697796"
														/>
													</svg>
													{`${formatNumber(threshold, 2)}${unit}`}
												</div>,
											);
										},
									},
									width: 2,
									dashStyle: "Solid",
								},
							],
			},
			plotOptions: {
				column: {
					minPointLength: 3,
					pointPadding: 0.1,
					borderWidth: 0,
					groupPadding: 0.4,
					animation: { duration: 0 },
					shadow: false,
				},
			},
			series: [
				{
					name: props.mainMetricLabel,
					type: "column",
					data: currentSeriesData,
					events: {
						click: handleSelectionClick(
							(newSelection) => {
								setSelectedIds(newSelection);
								emitSelectionChange(newSelection);
							},
							{ data: props.data, selectedIds, shiftSelectionIdRef },
						),
					},
				},
				{
					states: {
						inactive: { enabled: false },
						select: { color: themeCSSVars.palette_P700 },
						hover: { color: themeCSSVars.palette_B500 },
					},
					name: props.secondaryMetricLabel,
					type: "column",
					color: themeCSSVars.palette_B400,
					data: proposalSeriesData,
					pointWidth: calculatePointWidth({
						visibleCount,
						metricType: "secondary",
						metricState: "normal",
					}),
					events: {
						click: handleSelectionClick(
							(newSelection) => {
								setSelectedIds(newSelection);
								emitSelectionChange(newSelection);
							},
							{ data: props.data, selectedIds, shiftSelectionIdRef },
						),
					},
				},
				...(!props.threshold
					? []
					: [
							{
								name: "threshold-placeholder",
								type: "column",
								data: [["threshold", props.threshold]],
								color: "transparent",
								pointWidth: 1,
								pointInterval: 0,
								allowPointSelect: false,
								opacity: 0,
							} satisfies SeriesColumnOptions,
						]),
			],
			navigator: {
				enabled: true,
				height: 30,
				series: {
					color: themeCSSVars.palette_S300,
					opacity: 0.7,
				},
				xAxis: {
					labels: {
						enabled: false,
					},
				},
				yAxis: {
					labels: {
						enabled: false,
					},
				},
			},
			exporting: {
				enabled: false,
			},
		};
		// The props this reads, one by one, rather than `props` itself — which is a fresh object every render, so
		// depending on it gave these options a new identity every time, and Highcharts' React wrapper keys its
		// `chart.update(options, redraw, oneToOne)` on exactly that identity. `useEffectEvent` results are stable
		// by construction and so are absent below.
	}, [
		dragging,
		formatNumber,
		props.data,
		props.mainMetricLabel,
		props.secondaryMetricLabel,
		props.threshold,
		selectedIds,
		setSelectedIds,
		threshold,
		thresholdBoxId,
		unit,
		currentSeriesData,
		proposalSeriesData,
		visibleCount,
		tooltipId,
	]);

	return (
		<>
			{selectionRect && (
				<StackingContext.Consumer>
					{({ zIndex }) =>
						createPortal(
							<div
								className="fixed border bg-slate-500/50"
								style={{
									zIndex,
									left: selectionRect.x,
									top: selectionRect.y,
									width: selectionRect.width,
									height: selectionRect.height,
								}}
							/>,
							document.body,
						)
					}
				</StackingContext.Consumer>
			)}
			<ChartCardShell
				id={chartId}
				fullHeight={props.chartHeight === "full"}
				title={
					<div className="flex items-center">
						<ChartTitle>{props.chartTitle}</ChartTitle>
						<div className="w-4 mx-4 border-b border-b-black" />
						<div className="mr-4">
							<Text type="Body/M/Book" color={themeCSSVars.palette_N500} className="mr-2">
								Min
							</Text>
							{!Number.isNaN(min) && Math.abs(min) !== Infinity && (
								<Text type="Body/M/Bold" color={themeCSSVars.palette_N500}>
									{formatNumber(min, {
										minDecimalPlaces: 0,
										maxDecimalPlaces: 2,
									})}
									{unit}
								</Text>
							)}
						</div>
						<div className="mr-4">
							<Text type="Body/M/Book" color={themeCSSVars.palette_N500} className="mr-2">
								Max
							</Text>
							{!Number.isNaN(max) && Math.abs(max) !== Infinity && (
								<Text type="Body/M/Bold" color={themeCSSVars.palette_N500}>
									{formatNumber(max, {
										minDecimalPlaces: 0,
										maxDecimalPlaces: 2,
									})}
									{unit}
								</Text>
							)}
						</div>
						<div>
							<Text type="Body/M/Book" color={themeCSSVars.palette_N500} className="mr-2">
								Average
							</Text>
							{!Number.isNaN(avg) && Math.abs(avg) !== Infinity && (
								<Text type="Body/M/Bold" color={themeCSSVars.palette_N500}>
									{formatNumber(avg, {
										minDecimalPlaces: 0,
										maxDecimalPlaces: 2,
									})}
									{unit}
								</Text>
							)}
						</div>
					</div>
				}
				midActions={props.midActions}
				actions={props.actions}
			>
				<HighchartsReact
					containerProps={containerProps}
					highcharts={Highcharts}
					options={chartOptions}
					ref={highchartsRef}
				/>
			</ChartCardShell>
		</>
	);
}

/** @internal — exported for tests */
export function intersect(rect1: Vec2D & Rect2D, rect2: Vec2D & Rect2D) {
	return !(
		rect1.x + rect1.width < rect2.x ||
		rect1.y + rect1.height < rect2.y ||
		rect2.x + rect2.width < rect1.x ||
		rect2.y + rect2.height < rect1.y
	);
}

function domRectToRect2D(domRect: DOMRect): Vec2D & Rect2D {
	return {
		x: domRect.left,
		y: domRect.top,
		width: domRect.width,
		height: domRect.height,
	};
}

function calculatePlaceholderHeight(max: number, percentage: number = 0.1): number {
	return max * percentage;
}

export function MetricComparisonChartWall({
	status,
	...rest
}: MetricComparisonChartProps & {
	status: ChartWallStatus;
}) {
	return (
		<ChartWallSwitch
			status={status}
			chartTitle={rest.chartTitle}
			midActions={rest.midActions}
			actions={rest.actions}
			chartHeight={rest.chartHeight}
		>
			{() => <MetricComparisonChart {...rest} />}
		</ChartWallSwitch>
	);
}
